<template>
  <div
    class="py-6 space-y-6 h-full flex flex-col bg-gray-50 overflow-y-auto custom-scrollbar"
  >
    <!-- === HEADER & STATUS CONTROLS === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div
        class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6"
      >
        <div class="flex items-center gap-3">
          <div class="p-3 bg-primary-50 rounded-xl">
            <i class="pi pi-shopping-bag text-2xl text-primary-600"></i>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-gray-900 flex items-center">
              Commande #{{ orderData?.id ? truncateId(orderData.id) : '-' }}
              <div
                v-if="
                  orderData?.deliveryStatus && orderData?.status !== 'delivered'
                "
                class="ml-3 px-3 py-1 bg-blue-50 text-blue-600 border border-blue-200 rounded-full text-xs font-bold uppercase tracking-wider inline-block"
              >
                🚚 {{ orderData.deliveryStatus }}
              </div>
            </h1>
            <p class="text-sm text-gray-500 mt-1">
              Passée le
              {{
                orderData?.orderDate ? Commons.time(orderData.orderDate) : '-'
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Informations additionnelles d'en-tête (Livraison & Paiement) -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 pt-2"
      >
        <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
          <span
            class="text-[10px] text-gray-400 font-bold uppercase tracking-wider block"
            >ID Livraison</span
          >
          <span class="text-xs font-bold text-gray-800">{{
            orderData?.deliveryCode || orderData?.deliveryId || '-'
          }}</span>
        </div>
        <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
          <span
            class="text-[10px] text-gray-400 font-bold uppercase tracking-wider block"
            >Option de livraison</span
          >
          <span class="text-xs font-bold text-gray-800">{{
            orderData?.deliveryOption || '-'
          }}</span>
        </div>
        <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
          <span
            class="text-[10px] text-gray-400 font-bold uppercase tracking-wider block"
            >Méthode de paiement</span
          >
          <span class="text-xs font-bold text-gray-800">{{
            getPaymentMethod(orderData?.processor || orderData?.paymentMethod)
          }}</span>
        </div>
        <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
          <span
            class="text-[10px] text-gray-400 font-bold uppercase tracking-wider block"
            >ID Paiement / Ref</span
          >
          <span class="text-xs font-bold text-gray-800 truncate block">{{
            orderData?.paymentId || '-'
          }}</span>
        </div>
      </div>

      <div
        class="flex flex-wrap gap-4 pt-4 border-t border-gray-100 items-center justify-between"
      >
        <div class="flex flex-wrap gap-4">
          <div
            v-if="orderData?.paymentMethod !== 'cash'"
            class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100"
          >
            <span
              class="text-xs text-gray-500 font-medium uppercase tracking-wider"
              >Paiement:</span
            >
            <span
              class="text-sm font-semibold"
              :class="
                orderData?.isPaid ? 'text-success-600' : 'text-warning-600'
              "
            >
              {{ orderData?.isPaid ? 'Payé' : 'En attente' }}
            </span>
          </div>
          <div
            class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100"
          >
            <span
              class="text-xs text-gray-500 font-medium uppercase tracking-wider"
              >Statut global:</span
            >
            <span class="text-sm font-semibold text-gray-700 uppercase">{{
              orderData?.status || '-'
            }}</span>
          </div>
        </div>
        <button
          v-if="orderData?.id"
          @click="Copy.toClipboard(orderData.id, 'ID copié')"
          class="flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-100 transition-colors"
        >
          <i class="pi pi-copy text-xs text-gray-400"></i>
          <span class="text-xs font-medium text-gray-600">Copier l'ID</span>
        </button>
      </div>
    </div>

    <!-- === TIMELINE (VISUAL) === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 class="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
        <i class="pi pi-clock text-primary-500"></i>
        Suivi de la commande
      </h3>

      <div
        class="relative flex items-center justify-between max-w-4xl mx-auto px-4"
      >
        <!-- Connecting Line -->
        <div
          class="absolute left-0 right-0 h-0.5 bg-gray-100 -z-10 top-1/2 -translate-y-1/2 mx-10"
        ></div>
        <div
          class="absolute left-0 h-0.5 bg-primary-500 -z-10 top-1/2 -translate-y-1/2 transition-all duration-1000 mx-10"
          :style="{ width: timelineProgress + '%' }"
        ></div>

        <div
          v-for="(step, index) in timelineSteps"
          :key="step.key"
          class="flex flex-col items-center"
        >
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300"
            :class="[
              isStepCompleted(step.key, index)
                ? 'bg-primary-500 border-primary-500 text-white shadow-lg shadow-primary-200'
                : 'bg-white border-gray-200 text-gray-300',
            ]"
          >
            <i :class="step.icon" class="text-sm"></i>
          </div>
          <div class="mt-3 text-center">
            <p
              class="text-xs font-bold leading-tight"
              :class="
                isStepCompleted(step.key, index) ? 'text-gray-900' : 'text-gray-400'
              "
            >
              {{ step.label }}
            </p>
            <p
              v-if="getStepDisplayTime(step.key, index)"
              class="text-[10px] text-gray-500 mt-0.5 whitespace-nowrap"
            >
              {{ getStepDisplayTime(step.key, index) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- === CLIENT & RESTAURANT === -->
      <div class="space-y-6">
        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3
            class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"
          >
            <i class="pi pi-user text-blue-500"></i>
            Client
          </h3>
          <div class="flex items-center gap-4 mb-4">
            <div
              class="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold overflow-hidden"
            >
              <img
                v-if="orderData?.customerAvatar"
                :src="orderData.customerAvatar"
                class="w-full h-full object-cover"
              />
              <span v-else>{{
                (orderData?.customerName || 'C').charAt(0).toUpperCase()
              }}</span>
            </div>
            <div>
              <p class="font-bold text-gray-900">
                {{ orderData?.customerName || 'Client anonyme' }}
              </p>
              <p class="text-sm text-gray-500">
                {{ orderData?.countryCode || '' }}
                {{ orderData?.customerPhoneNumber || '-' }}
              </p>
              <p class="text-xs text-gray-400 mt-0.5">
                ID:
                {{
                  orderData?.customerId ? truncateId(orderData.customerId) : '-'
                }}
              </p>
            </div>
          </div>
          <div class="p-3 bg-gray-50 rounded-xl space-y-2">
            <div class="flex items-start gap-2">
              <i class="pi pi-map-marker text-gray-400 mt-1"></i>
              <div>
                <p class="text-sm font-medium text-gray-900">
                  Adresse de livraison
                </p>
                <p class="text-sm text-gray-600">
                  {{ orderData?.destinationAddress || '-' }}
                </p>
                <p
                  v-if="orderData?.destinationCity"
                  class="text-xs text-gray-500 mt-0.5"
                >
                  Ville: {{ orderData.destinationCity }}
                </p>
                <p
                  v-if="orderData?.destinationDescription"
                  class="text-xs text-gray-500 italic mt-0.5"
                >
                  {{ orderData.destinationDescription }}
                </p>
                <a
                  v-if="orderData?.destinationLat"
                  :href="`https://www.google.com/maps?q=${orderData.destinationLat},${orderData.destinationLng}`"
                  target="_blank"
                  class="text-xs text-primary-600 hover:underline flex items-center gap-1 mt-1"
                >
                  Voir sur la carte
                  <i class="pi pi-external-link text-[10px]"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3
            class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"
          >
            <i class="pi pi-home text-orange-500"></i>
            Restaurant
          </h3>
          <div class="flex items-center gap-4 mb-4">
            <div
              class="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 font-bold"
            >
              {{ (orderData?.storeName || 'R').charAt(0).toUpperCase() }}
            </div>
            <div>
              <p class="font-bold text-gray-900">
                {{ orderData?.storeName || '-' }}
              </p>
              <p class="text-xs text-gray-500">
                ID:
                {{ orderData?.storeId ? truncateId(orderData.storeId) : '-' }}
              </p>
            </div>
          </div>
          <div class="p-3 bg-gray-50 rounded-xl space-y-2">
            <div class="flex items-start gap-2">
              <i class="pi pi-map-marker text-gray-400 mt-1"></i>
              <div>
                <p class="text-sm font-medium text-gray-900">
                  Adresse de récupération
                </p>
                <p class="text-sm text-gray-600">
                  {{ orderData?.pickupAddress || '-' }}
                </p>
                <a
                  v-if="orderData?.pickupLat"
                  :href="`https://www.google.com/maps?q=${orderData.pickupLat},${orderData.pickupLng}`"
                  target="_blank"
                  class="text-xs text-primary-600 hover:underline flex items-center gap-1 mt-1"
                >
                  Voir sur la carte
                  <i class="pi pi-external-link text-[10px]"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- === ARTICLES & RECAP === -->
      <div class="space-y-6">
        <div
          class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm flex-1 flex flex-col"
        >
          <h3 class="text-lg font-bold text-gray-900 mb-4">
            Articles ({{ orderData?.products?.length || 0 }})
          </h3>
          <div class="space-y-4 flex-1">
            <div
              v-for="(product, index) in orderData?.products"
              :key="index"
              class="flex items-center gap-4 group"
            >
              <div
                class="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border border-gray-100 flex-shrink-0"
              >
                <ImgComponent
                  fallback="/images/logo.png"
                  :src="product?.image"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-bold text-gray-900 truncate">
                  {{ product?.name || '-' }}
                </p>
                <p class="text-sm text-gray-500">
                  Qté: {{ product?.quantity || 1 }} x
                  {{
                    Commons.formatNumber(
                      product?.unitPrice || product?.price || 0,
                      {
                        suffix: ' FCFA',
                      },
                    )
                  }}
                </p>
                <p v-if="product?.notes" class="text-xs text-gray-400 italic">
                  Note: {{ product.notes }}
                </p>
              </div>
              <div class="text-right">
                <p class="font-bold text-gray-900">
                  {{
                    Commons.formatNumber(
                      product?.totalPrice ||
                        (product?.price || product?.unitPrice || 0) *
                          (product?.quantity || 1),
                      { suffix: ' FCFA' },
                    )
                  }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="orderData?.drinks?.length"
            class="mt-6 border-t border-gray-100 pt-4"
          >
            <h4 class="text-sm font-bold text-gray-900 mb-3">
              Boissons ({{ orderData.drinks.length }})
            </h4>
            <div class="space-y-3">
              <div
                v-for="(drink, idx) in orderData.drinks"
                :key="idx"
                class="flex items-center gap-4"
              >
                <div
                  class="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100"
                >
                  <i class="pi pi-glass text-blue-400"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-bold text-gray-900 truncate">
                    {{ drink.name }}
                  </p>
                  <p class="text-xs text-gray-500">Qté: {{ drink.quantity }}</p>
                </div>
                <div class="text-right">
                  <p class="font-bold text-gray-900">
                    {{
                      Commons.formatNumber(
                        (drink.unitPrice || drink.price || 0) *
                          (drink.quantity || 1),
                        { suffix: ' FCFA' },
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section Détails Tarification & Commissions -->
          <div class="mt-8 pt-6 border-t border-gray-100 space-y-3">
            <div class="flex justify-between text-gray-600 text-sm">
              <span>Sous-total</span>
              <span class="font-medium text-gray-900">{{
                Commons.formatNumber(orderData?.subtotal ?? subTotal, {
                  suffix: ' FCFA',
                })
              }}</span>
            </div>
            <div class="flex justify-between text-gray-600 text-sm">
              <span>Frais de livraison</span>
              <span class="font-medium text-gray-900">{{
                Commons.formatNumber(orderData?.deliveryFee || 0, {
                  suffix: ' FCFA',
                })
              }}</span>
            </div>
            <div
              class="flex justify-between text-gray-600 text-sm"
              v-if="orderData?.serviceFee"
            >
              <span>Frais de service</span>
              <span class="font-medium text-gray-900">{{
                Commons.formatNumber(orderData.serviceFee, { suffix: ' FCFA' })
              }}</span>
            </div>
            <div
              class="flex justify-between text-success-600 text-sm"
              v-if="discountAmount > 0"
            >
              <span>Réduction</span>
              <span class="font-medium"
                >-{{
                  Commons.formatNumber(discountAmount, { suffix: ' FCFA' })
                }}</span
              >
            </div>

            <!-- Détails de pricing additionnels (Commissions & Payouts) -->
            <div
              v-if="orderData?.pricing"
              class="p-3 bg-gray-50 rounded-xl space-y-1.5 my-2 border border-gray-100 text-xs"
            >
              <div class="flex justify-between text-gray-500">
                <span>Commission Restaurant (Rapidoo):</span>
                <span class="font-semibold text-gray-800">{{
                  Commons.formatNumber(
                    orderData.pricing.commissionRapidooRestaurant || 0,
                    { suffix: ' FCFA' },
                  )
                }}</span>
              </div>
              <div class="flex justify-between text-gray-500">
                <span>Commission Livreur (Rapidoo):</span>
                <span class="font-semibold text-gray-800">{{
                  Commons.formatNumber(
                    orderData.pricing.commissionRapidooRider || 0,
                    { suffix: ' FCFA' },
                  )
                }}</span>
              </div>
              <div class="flex justify-between text-gray-500">
                <span>Reversement Restaurant (Payout):</span>
                <span class="font-semibold text-gray-800">{{
                  Commons.formatNumber(
                    orderData.pricing.restaurantPayout || 0,
                    { suffix: ' FCFA' },
                  )
                }}</span>
              </div>
              <div class="flex justify-between text-gray-500">
                <span>Reversement Livreur (Payout):</span>
                <span class="font-semibold text-gray-800">{{
                  Commons.formatNumber(orderData.pricing.riderPayout || 0, {
                    suffix: ' FCFA',
                  })
                }}</span>
              </div>
              <div class="flex justify-between text-gray-500">
                <span>Chiffre d'affaires Rapidoo:</span>
                <span class="font-semibold text-primary-600">{{
                  Commons.formatNumber(orderData.pricing.rapidooRevenue || 0, {
                    suffix: ' FCFA',
                  })
                }}</span>
              </div>
              <div
                class="flex justify-between text-gray-500 pt-1 border-t border-gray-200"
              >
                <span>Distance / Durée:</span>
                <span class="font-semibold text-gray-800"
                  >{{ orderData.pricing.distanceFormatted || '-' }} ({{
                    orderData.pricing.durationFormatted || '-'
                  }})</span
                >
              </div>
            </div>

            <div
              class="flex justify-between text-xl font-black text-gray-900 pt-3 border-t-2 border-primary-100"
            >
              <span>TOTAL</span>
              <span class="text-primary-600">{{
                Commons.formatNumber(
                  orderData?.totalPrice || orderData?.pricing?.total || 0,
                  {
                    suffix: ' FCFA',
                  },
                )
              }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="orderData?.customerNote"
          class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <h3
            class="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"
          >
            <i class="pi pi-comment text-yellow-500"></i>
            Note du client
          </h3>
          <p
            class="text-sm text-gray-700 italic bg-yellow-50 p-4 rounded-xl border border-yellow-100 leading-relaxed shadow-inner"
          >
            "{{ orderData.customerNote }}"
          </p>
        </div>

        <!-- Section Évaluation / Avis (si renseigné) -->
        <div
          v-if="orderData?.rating > 0 || orderData?.reviewComment"
          class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <h3
            class="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2"
          >
            <i class="pi pi-star text-amber-500"></i> Avis du client
          </h3>
          <div class="flex items-center gap-2 mb-2">
            <div class="flex text-amber-400">
              <i
                v-for="n in 5"
                :key="n"
                class="pi"
                :class="n <= orderData.rating ? 'pi-star-fill' : 'pi-star'"
              ></i>
            </div>
            <span class="text-sm font-bold text-gray-800"
              >{{ orderData.rating }}/5</span
            >
          </div>
          <p
            v-if="orderData?.reviewComment"
            class="text-sm text-gray-600 italic bg-gray-50 p-3 rounded-xl"
          >
            "{{ orderData.reviewComment }}"
          </p>
          <p
            v-if="orderData?.reviewCreatedAt"
            class="text-[10px] text-gray-400 mt-2"
          >
            Posté le {{ Commons.time(orderData.reviewCreatedAt) }}
          </p>
        </div>
      </div>
    </div>

    <!-- === SUIVI LIVRAISON & LIVREUR === -->
    <div
      class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm col-span-2"
    >
      <h3 class="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <i class="pi pi-car text-green-500"></i> Suivi Livraison en temps réel
      </h3>

      <!-- Map Container - Always Visible -->
      <div
        class="rounded-xl overflow-hidden border border-gray-100 shadow-inner bg-gray-50 mb-6 h-[400px]"
      >
        <Map ref="mapRef" @ready="onMapReady" />
      </div>

      <div v-if="orderData?.riderId || orderData?.riderName" class="space-y-4">
        <!-- En-tête livreur -->
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-green-600 font-bold text-xl border border-green-100"
          >
            {{ (orderData?.riderName || 'L').charAt(0).toUpperCase() }}
          </div>
          <div>
            <p class="font-bold text-gray-900">
              {{ orderData?.riderName || '-' }}
            </p>
            <p class="text-sm text-gray-500">
              {{
                orderData?.riderPhoneNumber ||
                orderData?.rider?.phone ||
                ''
              }}
            </p>
            <p v-if="orderData?.riderId" class="text-xs text-gray-400 mt-0.5">
              ID Livreur: {{ truncateId(orderData.riderId) }}
            </p>
          </div>
        </div>

        <!-- Infos financières & Véhicule du livreur -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
            <p
              class="text-[10px] text-gray-500 uppercase tracking-widest font-bold"
            >
              Commission
            </p>
            <p class="text-sm font-black text-gray-900">
              {{
                Commons.formatNumber(orderData?.riderCommission || 0, {
                  suffix: ' FCFA',
                })
              }}
            </p>
          </div>
          <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
            <p
              class="text-[10px] text-gray-500 uppercase tracking-widest font-bold"
            >
              Véhicule
            </p>
            <p class="text-sm font-bold text-gray-900 capitalize">
              {{ orderData?.riderVehicle || '-' }}
            </p>
          </div>
          <div class="p-3 bg-gray-50 rounded-xl border border-gray-100">
            <p
              class="text-[10px] text-gray-500 uppercase tracking-widest font-bold"
            >
              Distance / Durée estimée
            </p>
            <p class="text-sm font-bold text-gray-900">
              {{
                orderData?.distanceKm ?? orderData?.pricing?.distance ?? 0
              }}
              km ({{ orderData?.durationMin ?? 0 }} min)
            </p>
          </div>
        </div>

        <!-- Barre de Progression Animée -->
        <div class="pt-4 border-t border-gray-100">
          <div class="relative pt-4">
            <div
              class="h-2.5 bg-gray-100 rounded-full overflow-hidden shadow-inner"
            >
              <div
                class="h-full bg-green-500 transition-all duration-1000 ease-in-out relative"
                :style="{ width: `${percentDelivery}%` }"
              >
                <div class="absolute inset-0 bg-white/30 animate-pulse"></div>
              </div>
            </div>
            <!-- Icône Moto -->
            <div
              class="absolute -top-1 transition-all duration-1000 ease-in-out"
              :style="{
                left: `calc(${percentDelivery}% - 12px)`,
                opacity: percentDelivery > 0 ? 1 : 0.5,
              }"
            >
              <div
                class="w-8 h-8 bg-white rounded-full shadow-md border-2 border-green-500 flex items-center justify-center"
              >
                <i class="pi pi-motorcycle text-green-600 text-sm"></i>
              </div>
            </div>
          </div>

          <!-- Heures estimées -->
          <div class="flex justify-between mt-4">
            <div class="text-left">
              <p class="text-[9px] font-bold text-gray-400 uppercase">
                Assigné à
              </p>
              <p class="font-black text-xs text-gray-700">
                {{
                  actualDeliveryAssignedAt
                    ? Commons.time(actualDeliveryAssignedAt, 'HH:mm')
                    : '--:--'
                }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-[9px] font-bold text-green-600 uppercase">
                Arrivée estimée
              </p>
              <p class="font-black text-xs text-green-600">
                {{
                  orderData?.deliveryEstimatedArrival
                    ? Commons.time(orderData.deliveryEstimatedArrival, 'HH:mm')
                    : '--:--'
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300 pointer-events-auto"
      >
        <i class="pi pi-clock text-gray-400 text-2xl mb-2"></i>
        <p class="text-sm font-medium text-gray-600">Aucun livreur assigné</p>
        <p class="text-xs text-gray-500 mt-1 mb-4">
          La carte affiche le trajet théorique
        </p>
        <button
          @click="openRiderSelection"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-all shadow-sm"
        >
          Assigner manuellement
        </button>
      </div>
    </div>

    <!-- Hidden ActionTriggers -->
    <ActionTrigger
      ref="updateStatusTrigger"
      dataKey="updateOrderStatus"
      @success="onStatusSuccess"
      @failed="onStatusError"
    />

    <!-- Rider Selection Modal -->
    <Teleport to="body">
      <div
        v-if="showRiderModal"
        @click.self="showRiderModal = false"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm cursor-pointer"
      >
        <div
          class="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 h-[600px] flex flex-col"
        >
          <div
            class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50"
          >
            <h2 class="text-xl font-bold text-gray-900">Assigner un livreur</h2>
            <button
              @click="showRiderModal = false"
              class="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
            >
              <i class="pi pi-times"></i>
            </button>
          </div>

          <div class="p-4 border-b border-gray-100">
            <div class="relative">
              <i
                class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              ></i>
              <input
                v-model="riderSearch"
                type="text"
                placeholder="Rechercher par nom ou téléphone..."
                class="w-full pl-10 pr-4 py-2 bg-gray-100 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              />
            </div>
          </div>

          <div class="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
            <div
              v-if="loadingRiders"
              class="flex flex-col items-center justify-center h-full text-gray-400"
            >
              <i class="pi pi-spin pi-spinner text-2xl mb-2"></i>
              <p class="text-xs">Chargement des livreurs...</p>
            </div>
            <div
              v-else-if="filteredRiders.length === 0"
              class="flex flex-col items-center justify-center h-full text-gray-400"
            >
              <i class="pi pi-user-minus text-2xl mb-2"></i>
              <p class="text-xs">Aucun livreur trouvé</p>
            </div>
            <div
              v-for="rider in filteredRiders"
              :key="rider.id"
              @click="confirmRider(rider)"
              class="p-4 rounded-2xl border border-gray-100 hover:border-primary-200 hover:bg-primary-50 cursor-pointer transition-all flex items-center justify-between group"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold"
                >
                  {{ rider.name.charAt(0).toUpperCase() }}
                </div>
                <div>
                  <p
                    class="text-sm font-bold text-gray-900 group-hover:text-primary-700"
                  >
                    {{ rider.name }}
                  </p>
                  <p class="text-[10px] text-gray-500">
                    {{ rider.phone || 'Pas de numéro' }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <div class="text-right mr-2">
                  <p
                    class="text-[10px] font-bold text-gray-400 uppercase tracking-widest"
                  >
                    Note
                  </p>
                  <p class="text-xs font-black text-gray-700">
                    {{ rider.rating?.toFixed(1) || '0.0' }}
                  </p>
                </div>
                <div
                  class="w-2 h-2 rounded-full"
                  :class="
                    rider.status === 'online' ? 'bg-green-500' : 'bg-orange-400'
                  "
                  :title="rider.status === 'online' ? 'En ligne' : 'Occupé'"
                ></div>
                <i
                  class="pi pi-chevron-right text-gray-300 group-hover:text-primary-400"
                ></i>
              </div>
            </div>
          </div>

          <div
            class="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between"
          >
            <p class="text-[10px] text-gray-400 italic">
              Seuls les livreurs actifs et en ligne apparaissent ici.
            </p>
            <button
              @click="showRiderModal = false"
              class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold rounded-lg transition-all"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import Map from '../map/Map.vue'
import Commons from '@/shared/utils/commons'
import Copy from '@/shared/utils/copy'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import statuses from './statuses.json'
import { buildApiUrl, ADMIN_ENDPOINTS } from '../../configs/api.js'

const props = defineProps({
  orderData: {
    type: Object,
    default: () => ({}),
  },
})

const selectedStatus = ref(props.orderData?.status)
const updatingStatus = ref(false)
const updateStatusTrigger = ref(null)
const mapRef = ref(null)

const showRiderModal = ref(false)
const riderSearch = ref('')
const loadingRiders = ref(false)
const availableRiders = ref([])

const filteredRiders = computed(() => {
  if (!riderSearch.value) return availableRiders.value
  const s = riderSearch.value.toLowerCase()
  return availableRiders.value.filter(
    r =>
      r.name.toLowerCase().includes(s) ||
      (r.phone && r.phone.toLowerCase().includes(s)),
  )
})

// Récupère la date/heure de la première assignation (ou de la dernière assignation) depuis le timeline
const actualDeliveryAssignedAt = computed(() => {
  if (!props.orderData?.timeline || !Array.isArray(props.orderData.timeline)) {
    return props.orderData?.deliveryAssignedAt || null
  }

  // Chercher l'événement 'assigned' dans le timeline
  const assignedEvent = props.orderData.timeline.find(
    item => item.status === 'assigned',
  )

  if (assignedEvent) {
    // Vérifier s'il y a un timestamp imbqué dans les métadonnées ou à la racine de l'événement
    return assignedEvent.metadata?.timestamp || assignedEvent.timestamp || null
  }

  return props.orderData?.deliveryAssignedAt || null
})

async function openRiderSelection() {
  showRiderModal.value = true
  loadingRiders.value = true
  try {
    const url = buildApiUrl(ADMIN_ENDPOINTS.ADMIN_RIDERS, {
      queryParams: { status: 'active', limit: 100 },
    })
    const resp = await fetch(url)
    const raw = await resp.json()
    availableRiders.value = (raw?.data?.riders || []).filter(
      r => r.status !== 'offline',
    )
  } catch (e) {
    console.error(e)
  } finally {
    loadingRiders.value = false
  }
}

async function confirmRider(rider) {
  if (!confirm(`Assigner ${rider.name} à cette commande ?`)) return

  try {
    const url = buildApiUrl(ADMIN_ENDPOINTS.ADMIN_ASSIGN_RIDER, {
      pathParams: { orderId: props.orderData.id },
    })
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ riderId: rider.id }),
    })
    const data = await resp.json()
    if (data.success) {
      showRiderModal.value = false
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'Livreur assigné avec succès',
      })
      // Refresh order data
      Events.dispatch('ORDER_UPDATED')
    } else {
      throw new Error(data.error?.message || 'Erreur lors de l’assignation')
    }
  } catch (e) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: e.message,
    })
  }
}

const onMapReady = () => {
  traceRoute()
}

function traceRoute() {
  if (!mapRef.value || !props.orderData?.id) return

  const pickupLat = parseFloat(props.orderData.pickupLat) || 6.3571
  const pickupLng = parseFloat(props.orderData.pickupLng) || 2.3699
  const destLat = parseFloat(props.orderData.destinationLat) || 6.3832
  const destLng = parseFloat(props.orderData.destinationLng) || 2.4156

  // Marqueurs fixes
  mapRef.value.traceRouteWithPoints(
    `order-route-${props.orderData.id}`,
    {
      lat: pickupLat,
      lng: pickupLng,
      content: props.orderData.storeName || 'Restaurant',
      iconType: 'pickup',
    },
    {
      lat: destLat,
      lng: destLng,
      content: 'Destination',
      iconType: 'destination',
    },
  )

  updateRiderPosition()
}

function updateRiderPosition() {
  if (
    !mapRef.value ||
    (!props.orderData?.riderId && !props.orderData?.riderName)
  )
    return

  const rLat = parseFloat(
    props.orderData.riderLat || props.orderData.riderLocation?.lat,
  )
  const rLng = parseFloat(
    props.orderData.riderLng || props.orderData.riderLocation?.lng,
  )

  if (isNaN(rLat) || isNaN(rLng)) return

  mapRef.value.setMarker(
    {
      position: { lat: rLat, lng: rLng },
      content: props.orderData.riderName || 'Livreur',
      iconType: 'rider',
    },
    `order-route-${props.orderData.id}`,
    'rider-position',
  )
}

watch(
  () => props.orderData,
  newVal => {
    if (newVal) {
      updateRiderPosition()
    }
  },
  { deep: true },
)

const percentDelivery = computed(() => {
  const status = props.orderData?.status
  const deliveryStatus = props.orderData?.deliveryStatus

  if (status === 'delivered' || deliveryStatus === 'delivered') return 100

  const deliverySteps = {
    pending: 5,
    assigned: 25,
    pickupReachedAt: 45,
    picked_up: 60,
    on_the_way: 85,
    destinationReachedAt: 95,
  }

  return deliverySteps[deliveryStatus] || 0
})

watch(
  () => props.orderData?.status,
  newStatus => {
    selectedStatus.value = newStatus
  },
)

// Timeline Configuration
const timelineSteps = [
  { key: 'created', label: 'Passée', icon: 'pi pi-shopping-cart' },
  { key: 'accepted', label: 'Confirmée', icon: 'pi pi-check-circle' },
  { key: 'preparing', label: 'En cuisine', icon: 'pi pi-spinner' },
  { key: 'cooked', label: 'Prête', icon: 'pi pi-verified' },
  { key: 'deliveryStarted', label: 'En route', icon: 'pi pi-send' },
  { key: 'delivered', label: 'Livrée', icon: 'pi pi-gift' },
]

const timelineProgress = computed(() => {
  if (!props.orderData) return 0

  let lastCompletedIndex = -1

  timelineSteps.forEach((step, index) => {
    if (isStepCompleted(step.key, index)) {
      lastCompletedIndex = index
    }
  })

  if (lastCompletedIndex === -1) return 0
  if (lastCompletedIndex === timelineSteps.length - 1) return 100

  return (lastCompletedIndex / (timelineSteps.length - 1)) * 100
})

const subTotal = computed(() => {
  return (props.orderData?.products || []).reduce(
    (acc, p) => acc + (p.unitPrice || p.price || 0) * (p.quantity || 1),
    0,
  )
})

const discountAmount = computed(() => {
  const d = props.orderData?.discount
  if (!d) return 0
  if (typeof d === 'number') return d
  if (d.amount) return d.amount
  if (d.percentage) return (subTotal.value * d.percentage) / 100
  return 0
})

function getStepTimestamp(stepKey) {
  const altKeys = {
    created: ['pending', 'created'],
    accepted: ['confirmed', 'accepted', 'approved'],
    preparing: ['preparing'],
    cooked: ['ready', 'ready_for_pickup'],
    deliveryStarted: [
      'assigned',
      'picked_up',
      'on_the_way',
      'in_transit',
      'arrived',
    ],
    delivered: ['delivered', 'completed'],
  }

  let timestamp = props.orderData?.stepsTimeline?.[stepKey]

  if (!timestamp && altKeys[stepKey]) {
    for (const alt of altKeys[stepKey]) {
      if (props.orderData?.stepsTimeline?.[alt]) {
        timestamp = props.orderData.stepsTimeline[alt]
        break
      }
    }
  }

  return timestamp
}

// Map each possible order status to a numeric progression index (0–5)
const statusOrder = {
  pending: 0,
  created: 0,
  accepted: 1,
  confirmed: 1,
  approved: 1,
  preparing: 2,
  ready: 3,
  ready_for_pickup: 3,
  cooked: 3,
  assigned: 4,
  picked_up: 4,
  on_the_way: 4,
  in_transit: 4,
  arrived: 4,
  deliveryStarted: 4,
  delivered: 5,
  completed: 5,
}

function isStepCompleted(stepKey, stepIndex) {
  // A step is completed if it has a timestamp …
  if (getStepTimestamp(stepKey)) return true

  // … or if the current order status sits at or beyond this step's index
  const currentStatus = String(props.orderData?.status || '').toLowerCase()
  const currentIdx = statusOrder[currentStatus] ?? -1
  if (currentIdx >= stepIndex) return true

  // … or if any later step already has a timestamp (order jumped ahead)
  for (let j = stepIndex + 1; j < timelineSteps.length; j++) {
    if (getStepTimestamp(timelineSteps[j].key)) return true
  }

  return false
}

function formatTime(ts) {
  if (!ts) return ''
  return Commons.time(ts, 'HH:mm')
}

function getStepDisplayTime(stepKey, stepIndex) {
  const ts = getStepTimestamp(stepKey)
  if (ts) return formatTime(ts)
  if (isStepCompleted(stepKey, stepIndex)) return 'Validé'
  return ''
}

function truncateId(id) {
  if (!id || id.length <= 12) return id
  return id.substring(0, 8).toUpperCase()
}

function getPaymentMethod(processor) {
  const methods = {
    mobile_money: 'Mobile Money',
    wallet: 'Portefeuille',
    credit_card: 'Carte bancaire',
    cash: 'Espèces',
  }
  return methods[processor] || processor || 'Inconnue'
}

const updateStatus = () => {
  if (selectedStatus.value === props.orderData?.status) return
  updatingStatus.value = true
  updateStatusTrigger.value.trigger({
    id: props.orderData.id,
    status: selectedStatus.value,
  })
}

const onStatusSuccess = () => {
  updatingStatus.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Statut de la commande mis à jour',
  })
}

const onStatusError = err => {
  updatingStatus.value = false
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: err?.message || 'Une erreur est survenue',
  })
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
